import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import AddObservation from "./AddObservation";
import {
  deleteObservation,
  deleteRecommendation,
  getObservations,
  getRecommendations,
} from "../../services/observationsApi";
import AddRecommendationModal from "./AddRecommendationModal";
import { toast } from "react-toastify";
import { Dialog } from "@mui/material";

interface Observation {
  id: number;
  category: string;
  description: string;
  impact: "Positive" | "Negative" | "Neutral";
}

interface Recommendation {
  id: number;
  category: string;
  description: string;
  priority: "High" | "Medium" | "Low";
}

interface ObservationsRecommendationsSectionProps {
  isPrintMode?: boolean;
  dateFilter: {
    startDate: string;
    endDate: string;
  };
  clinicData: string;
}

const ObservationsRecommendationsSection: React.FC<
  ObservationsRecommendationsSectionProps
> = ({ isPrintMode = false, dateFilter, clinicData }) => {
  const location = useLocation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{
    id: number;
    type: "observation" | "recommendation";
  } | null>(null);
  const [observationsList, setObservationsList] = useState<Observation[]>([]);
  const [recommendationsList, setRecommendationsList] = useState<
    Recommendation[]
  >([]);
  const [loading, setLoading] = useState(true);

  // Extract month and year from dateFilter
  const startDate = new Date(dateFilter.startDate);
  const endDate = new Date(dateFilter.endDate);
  const month = endDate.getMonth() + 1; // getMonth() returns 0-11
  const year = endDate.getFullYear();

  const fetchObservations = async () => {
    try {
      const res = await getObservations(
        clinicData,
        month.toString(),
        year.toString()
      );
      if (res?.status?.msg === "Success") {
        setObservationsList(res.observations);
      }
    } catch (error) {
      console.error("Error fetching observations:", error);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const res = await getRecommendations(
        clinicData,
        month.toString(),
        year.toString()
      );
      if (res?.status?.msg === "Success") {
        setRecommendationsList(res.recommendations);
      }
    } catch (error) {
      console.error("Error fetching recommendations:", error);
    }
  };

  useEffect(() => {
    if (clinicData && dateFilter?.startDate && dateFilter?.endDate) {
      fetchObservations();
      fetchRecommendations();
    }
  }, [clinicData, month, year]);

  useEffect(() => {
    setLoading(false);
  }, [observationsList, recommendationsList]);

  const [isAddObservationModalOpen, setIsAddObservationModalOpen] =
    useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [observationData, setObservationData] = useState<any>(null);
  const [recommendationData, setRecommendationData] = useState<any>(null);
  const [isAddRecommendationModalOpen, setIsAddRecommendationModalOpen] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleAddObservation = async (
    newObservation: Omit<Observation, "id">
  ) => {
    fetchObservations();
    setIsAddObservationModalOpen(false);
  };

  const handleAddRecommendation = (
    newRecommendation: Omit<Recommendation, "id">
  ) => {
    fetchRecommendations();
    setIsAddRecommendationModalOpen(false);
  };

  // const handleDeleteObservation = async (id: number) => {
  //   try {
  //     await deleteObservation(id);
  //     fetchObservations();
  //   } catch (error) {
  //     console.error("Error deleting observation:", error);
  //   }
  // };

  // const handleDeleteRecommendation = async (id: number) => {
  //   try {
  //     await deleteRecommendation(id);
  //     fetchRecommendations();
  //   } catch (error) {
  //     console.error("Error deleting recommendation:", error);
  //   }
  // };

  // Helper function to render impact badge
  const renderImpactBadge = (impact: "Positive" | "Negative" | "Neutral") => {
    let colorClass = "";
    let backgroundColorClass = "";

    switch (impact) {
      case "Positive":
        colorClass = "text-white";
        backgroundColorClass = "bg-[#4dccc3]"; // Light teal
        break;
      case "Negative":
        colorClass = "text-white";
        backgroundColorClass = "bg-[#e05a5a]"; // Red
        break;
      case "Neutral":
        colorClass = "text-gray-800";
        backgroundColorClass = "bg-gray-200"; // Light gray
        break;
    }

    return (
      <span
        className={`px-2 py-1 rounded text-xs font-semibold uppercase ${colorClass} ${backgroundColorClass}`}
      >
        {impact}
      </span>
    );
  };

  // Helper function to render priority badge
  const renderPriorityBadge = (priority: "High" | "Medium" | "Low") => {
    let colorClass = "";
    let backgroundColorClass = "";

    switch (priority) {
      case "High":
        colorClass = "text-white";
        backgroundColorClass = "bg-[#e05a5a]"; // Red
        break;
      case "Medium":
        colorClass = "text-gray-800";
        backgroundColorClass = "bg-[#f3bb4d]"; // Orange/Yellow
        break;
      case "Low":
        colorClass = "text-white";
        backgroundColorClass = "bg-[#4dccc3]"; // Light teal
        break;
    }

    return (
      <span
        className={`px-2 py-1 rounded text-xs font-semibold uppercase ${colorClass} ${backgroundColorClass}`}
      >
        {priority}
      </span>
    );
  };

  const handleDeleteConfirm = async () => {
    setIsLoading(true);
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === "observation") {
        await deleteObservation(clinicData, { id: itemToDelete.id });
        fetchObservations();
      } else {
        await deleteRecommendation(clinicData, { id: itemToDelete.id });
        fetchRecommendations();
      }
      toast.success(`Successfully deleted ${itemToDelete.type}`);
      setIsLoading(false);
    } catch (error) {
      toast.error(`Failed to delete ${itemToDelete.type}`);
      console.error(`Error deleting ${itemToDelete.type}:`, error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  // Use isPrintMode prop to set class name
  const sectionClassName = `section ${isPrintMode ? "print-mode" : ""}`;

  return (
    <>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-[#194e5b] mb-4">
          Observations and Recommendations
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Observations List */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-[#194e5b]">
                Observations
              </h3>
              {!isPrintMode && (
                <button
                  className="btn btn-sm text-white rounded-md"
                  style={{ backgroundColor: "#0E3843" }}
                  onClick={() => {
                    setIsAddObservationModalOpen(true);
                    setIsEdit(false);
                    setObservationData(null);
                  }}
                >
                  ➕ Add Observation
                </button>
              )}
            </div>

            <div className="observations-list space-y-4">
              {observationsList.map((observation) => (
                <div
                  key={observation.id}
                  className="observation-card bg-white p-4 rounded-lg shadow-sm"
                >
                  <div className="flex justify-between items-center mb-2">
                    <strong className="text-[#194e5b] text-base">
                      {observation.category}
                    </strong>
                    <div className="flex gap-2">
                      {renderImpactBadge(observation.impact)}
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setObservationData(observation);
                            setIsAddObservationModalOpen(true);
                            setIsEdit(true);
                          }}
                          className="btn btn-sm btn-secondary"
                          style={{
                            padding: "2px 8px",
                            backgroundColor: "#4CC9C0",
                            border: "none",
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setItemToDelete({
                              id: observation.id,
                              type: "observation",
                            });
                            setDeleteDialogOpen(true);
                          }}
                          className="btn btn-sm"
                          style={{
                            padding: "2px 8px",
                            backgroundColor: "#E0585B",
                            border: "none",
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="text-gray-700 text-sm h-fit mb-3">
                    {observation.description}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations List */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-[#194e5b]">
                Recommendations
              </h3>
              {!isPrintMode && (
                <button
                  className="btn btn-sm text-white rounded-md"
                  style={{ backgroundColor: "#0E3843" }}
                  onClick={() => {
                    setIsAddRecommendationModalOpen(true);
                    setIsEdit(false);
                    setRecommendationData(null);
                  }}
                >
                  ➕ Add Recommendation
                </button>
              )}
            </div>

            <div className="recommendations-list space-y-4">
              {recommendationsList.map((recommendation) => (
                <div
                  key={recommendation.id}
                  className="recommendation-card bg-white p-4 rounded-lg shadow-sm"
                >
                  <div className="flex justify-between items-center mb-2">
                    <strong className="text-[#194e5b] text-base">
                      {recommendation.category}
                    </strong>
                    <div className="flex gap-2">
                      {renderPriorityBadge(recommendation.priority)}
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setRecommendationData(recommendation);
                            setIsAddRecommendationModalOpen(true);
                            setIsEdit(true);
                          }}
                          className="btn btn-sm btn-secondary"
                          style={{
                            padding: "2px 8px",
                            backgroundColor: "#4CC9C0",
                            border: "none",
                          }}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setItemToDelete({
                              id: recommendation.id,
                              type: "recommendation",
                            });
                            setDeleteDialogOpen(true);
                          }}
                          className="btn btn-sm"
                          style={{
                            padding: "2px 8px",
                            backgroundColor: "#E0585B",
                            border: "none",
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="text-gray-700 text-sm mb-3">
                    {recommendation.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Observation Modal - only in edit mode */}
        {/* {!isPrintMode && (
        <ObservationModal
          isOpen={isAddObservationModalOpen}
          onClose={() => setIsAddObservationModalOpen(false)}
          onSave={handleAddObservation}
        />
      )} */}

        {/* Recommendation Modal - only in edit mode */}
        {/* {!isPrintMode && (
        <RecommendationModal
          isOpen={isAddRecommendationModalOpen}
          onClose={() => setIsAddRecommendationModalOpen(false)}
          onSave={handleAddRecommendation}
        />
      )} */}
      </div>
      <AddObservation
        clinicUniqueId={clinicData}
        open={isAddObservationModalOpen}
        onClose={() => setIsAddObservationModalOpen(false)}
        onSave={handleAddObservation}
        dateFilter={dateFilter}
        observationData={observationData}
        isEdit={isEdit}
      />
      <AddRecommendationModal
        clinicUniqueId={clinicData}
        open={isAddRecommendationModalOpen}
        onClose={() => setIsAddRecommendationModalOpen(false)}
        onSave={handleAddRecommendation}
        dateFilter={dateFilter}
        isEdit={isEdit}
        recommendationData={recommendationData}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <div className="p-4">
          <h3
            className="text-lg font-medium text-gray-900"
            id="alert-dialog-title"
          >
            Delete {itemToDelete?.type}
          </h3>
          <div className="mt-2">
            <p className="text-sm text-gray-500">
              Are you sure you want to delete this {itemToDelete?.type}? This
              action cannot be undone.
            </p>
          </div>
          <div className="mt-4 flex justify-end space-x-3">
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700"
              onClick={handleDeleteConfirm}
            >
              {isLoading ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ObservationsRecommendationsSection;
